/**
 * Shopify-style Experience View for Customer
 *
 * Gives the Customer detail page a bespoke, calm "cockpit" layout instead of
 * the raw ERPNext form grid — while keeping EVERY ERPNext Customer field
 * available (unmapped fields are auto-collected into the "More Info" tab by
 * convertFormViewToTabStructure).
 *
 * Registered (no `context`) so resolveView() picks it as the generic active
 * view for Customer. Rendered by ExperienceView → ShopifyStyleRenderer →
 * GenericSection (no custom widgets required for the main column; bespoke
 * panels live in CustomerDetailSidebar).
 *
 * Fieldnames are validated against meta in dev mode. Any field that does not
 * exist in the tenant's Customer meta is silently skipped, so this is safe
 * across ERPNext minor versions and custom-field variations.
 */

import type { DocTypeView } from '../types';

export const customerShopifyView: DocTypeView = {
  id: 'customer-shopify-v1',
  doctype: 'Customer',
  style: 'shopify',
  // No context → generic active view for Customer
  status: 'active',
  label: 'Customer – Shopify Style',
  description:
    'Shopify-style customer cockpit. Identity, tax, defaults, credit, and classification grouped into calm cards. Bespoke financial / order-history / contact panels render in the right sidebar.',

  formView: {
    sections: [
      // ── Details tab ──────────────────────────────────────────────────────
      // 1. Overview — who this customer is
      {
        id: 'customer-overview',
        title: 'Customer',
        description: 'Identity and classification.',
        layout: [
          {
            columns: [
              { fieldname: 'customer_name', mode: 'edit', visibility: 'default', width: 'full' },
              { fieldname: 'customer_type', mode: 'edit', visibility: 'default', width: '1/2' },
              { fieldname: 'customer_group', mode: 'edit', visibility: 'default', width: '1/2' },
              { fieldname: 'territory', mode: 'edit', visibility: 'default', width: '1/2' },
              { fieldname: 'salutation', mode: 'read', visibility: 'advanced', width: '1/2' },
              { fieldname: 'gender', mode: 'read', visibility: 'advanced', width: '1/2' },
            ],
          },
        ],
      },

      // 2. Contact & address (raw ERPNext links — polished cards are in the sidebar)
      {
        id: 'customer-contact',
        title: 'Contact & Address',
        description: 'Primary contact and address on file.',
        layout: [
          {
            columns: [
              { fieldname: 'customer_primary_contact', mode: 'read', visibility: 'default', width: 'full' },
              { fieldname: 'email_id', mode: 'read', visibility: 'default', width: '1/2' },
              { fieldname: 'mobile_no', mode: 'read', visibility: 'default', width: '1/2' },
              { fieldname: 'customer_primary_address', mode: 'read', visibility: 'default', width: 'full' },
              { fieldname: 'primary_address', mode: 'read', visibility: 'default', width: 'full' },
              { fieldname: 'website', mode: 'edit', visibility: 'advanced', width: 'full' },
            ],
          },
        ],
      },

      // ── Financial tab ────────────────────────────────────────────────────
      // 3. Defaults — currency, price list, terms
      {
        id: 'customer-defaults',
        title: 'Selling Defaults',
        description: 'Currency, price list, and payment terms applied to new orders.',
        layout: [
          {
            columns: [
              { fieldname: 'default_currency', mode: 'edit', visibility: 'default', width: '1/2' },
              { fieldname: 'default_price_list', mode: 'edit', visibility: 'default', width: '1/2' },
              { fieldname: 'payment_terms', mode: 'edit', visibility: 'default', width: 'full' },
              { fieldname: 'default_sales_partner', mode: 'read', visibility: 'advanced', width: '1/2' },
              { fieldname: 'default_commission_rate', mode: 'read', visibility: 'advanced', width: '1/2' },
            ],
          },
        ],
      },

      // 4. Credit limits (child table)
      {
        id: 'customer-credit',
        title: 'Credit Limit',
        description: 'Per-company credit limit and bypass settings.',
        layout: [
          {
            columns: [
              { fieldname: 'credit_limits', mode: 'edit', visibility: 'default', width: 'full' },
            ],
          },
        ],
      },

      // NOTE: loyalty_program / loyalty_program_tier are intentionally NOT mapped
      // into a section here — they surface (editable) in the auto "More Info"
      // section, while the rich loyalty view lives in the dedicated Loyalty tab
      // (points balance, history, redemptions). See useLoyaltyTab / LoyaltyTab.

      // ── More Info tab ────────────────────────────────────────────────────
      // 6. Tax
      {
        id: 'customer-tax',
        title: 'Tax',
        description: 'Tax identification and category.',
        layout: [
          {
            columns: [
              { fieldname: 'tax_id', mode: 'edit', visibility: 'default', width: 'full' },
              { fieldname: 'tax_category', mode: 'read', visibility: 'default', width: '1/2' },
              { fieldname: 'tax_withholding_category', mode: 'read', visibility: 'default', width: '1/2' },
            ],
          },
        ],
      },

      // 7. Classification
      {
        id: 'customer-classification',
        title: 'Classification',
        description: 'Market segment, industry, and ownership.',
        layout: [
          {
            columns: [
              { fieldname: 'market_segment', mode: 'read', visibility: 'default', width: '1/2' },
              { fieldname: 'industry', mode: 'read', visibility: 'default', width: '1/2' },
              { fieldname: 'account_manager', mode: 'read', visibility: 'default', width: 'full' },
            ],
          },
        ],
      },

      // 8. Details / notes
      {
        id: 'customer-details',
        title: 'Additional Details',
        description: 'Free-form details and preferred language.',
        layout: [
          {
            columns: [
              { fieldname: 'customer_details', mode: 'edit', visibility: 'default', width: 'full' },
              { fieldname: 'language', mode: 'read', visibility: 'advanced', width: '1/2' },
            ],
          },
        ],
      },

      // 9. Flags
      {
        id: 'customer-flags',
        title: 'Status & Flags',
        description: 'Account status and document requirements.',
        layout: [
          {
            columns: [
              { fieldname: 'disabled', mode: 'edit', visibility: 'default', width: '1/2' },
              { fieldname: 'is_internal_customer', mode: 'read', visibility: 'advanced', width: '1/2' },
              { fieldname: 'is_frozen', mode: 'read', visibility: 'advanced', width: '1/2' },
              { fieldname: 'so_required', mode: 'read', visibility: 'advanced', width: '1/2' },
              { fieldname: 'dn_required', mode: 'read', visibility: 'advanced', width: '1/2' },
            ],
          },
        ],
      },
    ],

    // Any ERPNext Customer field we didn't map above is auto-collected into a
    // "more-info-*" section by convertFormViewToTabStructure, so nothing is lost.
    customFieldBucket: {
      enabled: false,
      sectionTitle: 'Additional ERP Fields',
      visibilityDefault: 'collapsed',
    },
  },

  createdBy: 'system',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};
